
import Vue from 'vue';
import { useStore as useProductStore } from '@/stores/v2/product';
import { useStore as useColorsManagementStore } from '@/stores/v2/colorsManagement';
import { useStore as useMirrorStore } from '@/stores/v2/mirror';
import { useStore as useHistoryStore } from '@/stores/v2/history';
import { useStore as useLangStore } from '@/stores/v2/lang';
import { useStore as useNotificationStore } from '@/stores/v2/notification';
import { useStore as useShapeStore } from '@/stores/v2/shape';
import { mapState, mapActions } from 'pinia';
import PrintPositionBox from '../PositionsManagement/PrintPositionBox.vue';
import Add32 from '@carbon/icons-vue/es/add/32';
import PrintPositionsModal from './PrintPositionsModal.vue';
import { generateEventPayloadV2 } from '@/utils/generatePriceParamsEventPayload';
import { createStateSnapshot } from '@/utils/historyHelperV2';
import { ProductPrintPositionAddModel } from '@/typings';
import { CvModal } from '@carbon/vue';

export default Vue.extend({
  name: 'PrintPositions',
  components: {
    PrintPositionsModal,
    PrintPositionBox,
    Add32,
    CvModal,
  },

  props: {
    sapCustomerId: {
      type: String,
      required: true,
    },
    tok: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      editId: '',
      yesNoDialog: false,
      positionToDelete: {} as ProductPrintPositionAddModel,
    };
  },

  computed: {
    ...mapState(useProductStore, [
      'data',
      'activePosition',
      'printPositions',
      'selectedPrintPosition',
      'unavailablePrintPositions',
    ]),
    ...mapState(useLangStore, ['languageInUse']),

    isAddEnabled(): boolean {
      if (this.unavailablePrintPositions && this.data.productPrintPositions) {
        return (
          this.unavailablePrintPositions.length <
          this.data.productPrintPositions.length
        );
      }
      return true;
    },

    techniqueNameTranslated(): string {
      const position = this.printPositions?.find(
        (p) => p.id === this.positionToDelete.positionId
      );
      const technique = position?.printTechniques.find((technique) => {
        return technique.id === this.positionToDelete.technique.id;
      });
      const techniqueName = technique?.printTechnique.nameTranslations.find(
        ({ language }) => language.code === this.languageInUse
      )?.text;
      return techniqueName || this.selectedPrintPosition.techniqueName || '';
    },

    isDeleteDisabled(): boolean {
      return this.printPositions.length == 1;
    },
  },

  methods: {
    ...mapActions(useProductStore, [
      'deletePrintPosition',
      'setPreviousManagedPrintPositionId',
    ]),
    ...mapActions(useMirrorStore, [
      'removePositionMirrorSettings',
      'removeMirrorShapes',
    ]),
    ...mapActions(useColorsManagementStore, ['getAvailablePmsColors']),
    ...mapActions(useHistoryStore, ['recordHistory']),
    ...mapActions(useNotificationStore, ['runValidators']),
    ...mapActions(useShapeStore, ['removeShapesForPosition']),

    handleLoadingNotificationsAndChecks(loading: any) {
      this.$emit('loadingNotificationsAndChecks', loading);
    },

    openModal(id: string) {
      this.editId = id;
      this.$modal.show('print-positions-modal');
    },

    actionDeletePrintPosition(position: ProductPrintPositionAddModel) {
      this.positionToDelete = position;
      this.yesNoDialog = true;
    },

    actionEditPrintPosition(id: string) {
      this.setPreviousManagedPrintPositionId(id);
      this.openModal(id);
    },

    async onDeletePrintPosition(deleteId: string) {
      this.removeShapesForPosition(this.positionToDelete.positionId);

      this.removeMirrorShapes(this.positionToDelete.positionId);

      await this.deletePrintPosition(this.positionToDelete.positionId);

      this.removePositionMirrorSettings(deleteId);

      const eventPayload = generateEventPayloadV2();

      this.$emit('wizard:price-params-changed', {
        productPrintPositions: eventPayload,
      });

      await this.getAvailablePmsColors();

      this.runValidators();

      const snapshot = createStateSnapshot();
      this.recordHistory(snapshot);
    },
  },
});
